import { pageCTADefaultValues } from '@/constants/bot';
import type { ICTAOption, IPageCTAForm, IPageLinkSearchResult } from '@/types/page-cta';

type PagePopupFormValues = Pick<
  IPageCTAForm,
  'popup_headline' | 'popup_greeting'
  | 'stored_popup_headline' | 'stored_popup_greeting'
  | 'option_a' | 'option_b' | 'option_c'
>;

/**
 * CTA Settings is a bulk editor: once one page is selected, the form becomes the
 * shared configuration that will be applied to every additional target. Hydrate
 * only the first page so adding another URL cannot silently replace that draft
 * with the second page's saved values.
 */
export const shouldHydrateStoredPageConfig = (selectedUrls: string[]): boolean => (
  selectedUrls.length === 0
);

const hydrateOption = (
  stored: ICTAOption | null | undefined,
  fallback: ICTAOption,
): ICTAOption => {
  if (!stored) {
    return {
      ...fallback,
      config: { ...fallback.config },
      isRequired: false,
    };
  }

  return {
    ...fallback,
    ...stored,
    config: { ...fallback.config, ...(stored.config || {}) },
    isRequired: Boolean(stored.type),
  };
};

/** Hydrate CTA Settings with what is actually saved for the selected page. */
export const getStoredPagePopupFormValues = (page: IPageLinkSearchResult): PagePopupFormValues => ({
  popup_headline: page.cached_content || '',
  popup_greeting: page.button_text || '',
  // Same values, kept untouched so validation can distinguish a page that never had
  // popup copy from one whose copy the vendor is deleting.
  stored_popup_headline: page.cached_content || '',
  stored_popup_greeting: page.button_text || '',
  option_a: hydrateOption(page.options?.option_a, pageCTADefaultValues.option_a),
  option_b: hydrateOption(page.options?.option_b, pageCTADefaultValues.option_b),
  option_c: hydrateOption(page.options?.option_c, pageCTADefaultValues.option_c),
});
